/**
 * RiftSurge (Fase 10 · §33-40, §48) — dificuldade OPCIONAL para regiões concluídas.
 * NÃO é daily/energia/timer/streak (§34, §47): o jogador ativa quando quer e ignora se
 * quiser. Aplica modificadores LEVES (nunca HP×10 §36) e recompensa maestria/loot/essência.
 * Estado é por SESSÃO (o mundo sempre inicia em estado normal; o "max tier" vem da maestria).
 */
import { GameConfig } from "../config/GameConfig";

type SurgeTier = (typeof GameConfig.riftSurge.tiers)[number];

export class RiftSurge {
  private activeRegion: string | null = null;
  private tier = 0;
  private kills = 0;
  private elites = 0;
  private done = false;

  private cfg(): SurgeTier | null {
    return this.tier > 0 ? GameConfig.riftSurge.tiers[this.tier - 1] : null;
  }

  activate(region: string, tier: number): void {
    this.activeRegion = region;
    this.tier = Math.max(1, Math.min(GameConfig.riftSurge.tiers.length, tier));
    this.kills = 0;
    this.elites = 0;
    this.done = false;
  }
  deactivate(): void {
    this.activeRegion = null;
    this.tier = 0;
    this.kills = 0;
    this.elites = 0;
    this.done = false;
  }
  isActiveIn(region: string): boolean {
    return this.activeRegion === region && this.tier > 0;
  }
  activeTier(region: string): number {
    return this.activeRegion === region ? this.tier : 0;
  }
  get anyActive(): boolean {
    return this.tier > 0;
  }
  get currentRegion(): string | null {
    return this.activeRegion;
  }

  // ---- modificadores (neutros quando não ativo na região) ----
  hpMult(region: string): number {
    return this.isActiveIn(region) ? this.cfg()!.hpMult : 1;
  }
  dmgMult(region: string): number {
    return this.isActiveIn(region) ? this.cfg()!.dmgMult : 1;
  }
  eliteChance(region: string): number {
    return this.isActiveIn(region) ? this.cfg()!.eliteChance : 0;
  }
  masteryMult(region: string): number {
    return this.isActiveIn(region) ? this.cfg()!.masteryMult : 1;
  }
  lootMult(region: string): number {
    return this.isActiveIn(region) ? this.cfg()!.lootMult : 1;
  }
  essenceMult(region: string): number {
    return this.isActiveIn(region) ? this.cfg()!.essenceMult : 1;
  }

  // ---- objetivo (§54): X kills + Y elites ----
  recordKill(region: string, isElite: boolean): void {
    if (!this.isActiveIn(region) || this.done) return;
    this.kills++;
    if (isElite) this.elites++;
  }
  objective(region: string): { kills: number; needKills: number; elites: number; needElites: number } | null {
    const c = this.cfg();
    if (!this.isActiveIn(region) || !c) return null;
    return { kills: this.kills, needKills: c.objKills, elites: this.elites, needElites: c.objElites };
  }
  /** true (uma única vez) quando o objetivo é cumprido — dispara a recompensa do Surge. */
  checkComplete(region: string): boolean {
    const o = this.objective(region);
    if (!o || this.done) return false;
    if (o.kills >= o.needKills && o.elites >= o.needElites) {
      this.done = true;
      return true;
    }
    return false;
  }
  get isComplete(): boolean {
    return this.done;
  }
}
